import { Arrays } from "../../core/Arrays";
import { Customer, ICustomer } from "./Customer";
import { License, ILicense } from "./License";

export enum BillPartType { Product, Shipping, Billing }

export class LicenseBill implements ILicenseBill {
    constructor(bill?: ILicenseBill) {
        if (bill) {
            if (bill.parentBillPart)
                this.parentBillPart = bill.parentBillPart instanceof LicenseBill ? bill.parentBillPart : new LicenseBill(bill.parentBillPart);
            if (bill.billPartType != null)
                this.billPartType = bill.billPartType;
            if (bill.billParts)
                this.billParts = bill.billParts.map((bp) => bp instanceof LicenseBill ? bp : new LicenseBill(bp));
            if (bill.licenses)
                this.licenses = bill.licenses.map((l) => l instanceof License ? l : new License(l));
            this.billDate = bill.billDate;
            this.customerNumber = bill.customerNumber;
            if (bill.customer)
                this.customer = bill.customer instanceof Customer ? bill.customer : new Customer(bill.customer);
            this.taxSchedule = bill.taxSchedule;
            this.manualDiscountAmount = bill.manualDiscountAmount;
            this.freightAmount = bill.freightAmount;
            this.currencyCode = bill.currencyCode;
            this.priceCode = bill.priceCode;

            this.productNumber = bill.productNumber;
            this.title = bill.title;
            this.unitCode = bill.unitCode;
            if (bill.quantity != null)
                this.quantity = bill.quantity;
            this.unitPrice = bill.unitPrice;
            this.volumeDiscountAmount = bill.volumeDiscountAmount;
            this.manualDiscountItemAmount = bill.manualDiscountItemAmount;
            this.taxAmount = bill.taxAmount;
        }
    }

    parentBillPart: LicenseBill;
    billPartType: BillPartType = BillPartType.Product;
    billParts: LicenseBill[] = [];

    licenses: License[] = [];

    billDate: string;
    customerNumber: string;
    customer: Customer;
    taxSchedule: string;
    manualDiscountAmount: number = 0;
    freightAmount: number = 0;
    currencyCode: string;
    priceCode?: string;

    productNumber: string;
    title: string;
    unitCode: string;
    unitPrice: number = 0;
    volumeDiscountAmount: number = 0;
    manualDiscountItemAmount: number = 0;
    taxAmount: number = 0;

    toJSON(): object {
        // return generic object with properties to stringify
        return {
            parentBillPart: this.parentBillPart,
            billPartType: this.billPartType,
            billParts: this.billParts,
            licenses: this.licenses,

            billDate: this.billDate,

            customerNumber: this.customerNumber,
            customer: this.customer,
            taxSchedule: this.taxSchedule,
            manualDiscountAmount: this.manualDiscountAmount,
            freightAmount: this.freightAmount,
            currencyCode: this.currencyCode,
            priceCode: this.priceCode,

            productNumber: this.productNumber,
            title: this.title,
            unitCode: this.unitCode,
            unitPrice: this.unitPrice,
            volumeDiscountAmount: this.volumeDiscountAmount,
            manualDiscountItemAmount: this.manualDiscountAmount,

            quantity: this._quantity != null ? this._quantity : undefined
        };
    }

    /* Calculated Properties */
    private _quantity: number;
    get quantity(): number {
        if (this._quantity)
            return this._quantity;
        return Arrays.sum(this.billParts, item => item.quantity);
    }
    set quantity(value: number) {
        this._quantity = value;
    }

    get baseAmount(): number {
        return this.unitPrice * this.quantity;
    }
    get discountAmount(): number {
        return this.volumeDiscountAmount + this.manualDiscountItemAmount;
    } 

    get discountedBaseAmount(): number {
        return this.baseAmount - this.discountAmount;
    }

    get unitDiscountAmount(): number {  
        return this.quantity > 0 ? this.discountAmount / this.quantity : 0;
    }

    get extendedAmount(): number { 
        return this.discountedBaseAmount + this.taxAmount;
    }

    get totalItemAmount(): number {
        return this.billPartType === BillPartType.Billing
            ? Arrays.sum(this.billParts, (item: LicenseBill) => item.totalItemAmount)
            : (this.billPartType === BillPartType.Shipping
                ? Arrays.sum(this.billParts, (item: LicenseBill) => item.baseAmount)
                : this.baseAmount);
    }

    get totalDiscountedItemAmount(): number {
        return this.billPartType === BillPartType.Billing
            ? Arrays.sum(this.billParts, (item: LicenseBill) => item.totalDiscountedItemAmount)
            : (this.billPartType === BillPartType.Shipping
                ? Arrays.sum(this.billParts, (item: LicenseBill) => item.discountedBaseAmount)
                : this.discountedBaseAmount);
    }

    get totalItemUnitDiscountAmount(): number {
        return this.billPartType === BillPartType.Billing
            ? Arrays.sum(this.billParts, (item: LicenseBill) => item.totalItemUnitDiscountAmount)
            : (this.billPartType === BillPartType.Shipping
                ? Arrays.sum(this.billParts, (item: LicenseBill) => item.unitDiscountAmount)
                : this.unitDiscountAmount);
    }

    get totalItemDiscountAmount(): number {
        return this.billPartType === BillPartType.Billing
            ? Arrays.sum(this.billParts, (item: LicenseBill) => item.totalItemDiscountAmount)
            : (this.billPartType === BillPartType.Shipping
                ? Arrays.sum(this.billParts, (item: LicenseBill) => item.discountAmount)
                : this.discountAmount);
    }

    get totalDiscountAmount() {
        return this.billPartType === BillPartType.Billing
            ? Arrays.sum(this.billParts, (item: LicenseBill) => item.totalDiscountAmount)
            : (this.billPartType === BillPartType.Shipping
                ? this.totalItemDiscountAmount + this.manualDiscountAmount
                : this.discountAmount);
    }

    get totalTaxAmount(): number {
        return this.billPartType == BillPartType.Billing
            ? Arrays.sum(this.billParts, (item: LicenseBill) => item.totalTaxAmount)
            : (this.billPartType === BillPartType.Shipping
                ? Arrays.sum(this.billParts, (item: LicenseBill) => item.taxAmount)
                : this.taxAmount);
    }

    get totalAmount(): number {
        return this.billPartType === BillPartType.Billing
            ? Arrays.sum(this.billParts, (item: LicenseBill) => item.totalAmount)
            : (this.billPartType === BillPartType.Shipping
                ? this.totalItemAmount - this.totalDiscountAmount + this.freightAmount + this.totalTaxAmount
                : this.extendedAmount);
    }
}

export interface ILicenseBill {
    parentBillPart: ILicenseBill;
    billPartType: BillPartType;
    billParts: ILicenseBill[];

    licenses: ILicense[];

    billDate: string;
    customerNumber: string;
    customer: ICustomer;
    taxSchedule: string;
    manualDiscountAmount: number;
    freightAmount: number;
    currencyCode: string;
    priceCode?: string;

    productNumber: string;
    title: string;
    unitCode: string;
    quantity: number;
    unitPrice: number;
    volumeDiscountAmount: number;
    manualDiscountItemAmount: number;
    taxAmount: number;
}